import type { FetchedField } from "./schemaApi";

// Builds field/schema payloads that satisfy the backend Joi validators
// (see schema-service/api/request_schemas/v1/createFieldSchema.js,
// createSchemaModelSchema.js, updateFieldSchema.js, updateSchemaModelSchema.js).
// Every Joi-required boolean is defaulted here so an LLM-generated proposal
// that omits a flag still validates.

export interface AssistantFieldInput {
    name: string;
    dataType: string;
    description?: string;
    isPrimaryKey?: boolean;
    isSortKey?: boolean;
    isRequired?: boolean;
    isPII?: boolean;
}

export interface AssistantUpdateFieldInput {
    fieldName: string;
    newName?: string;
    dataType?: string;
    description?: string;
    isPrimaryKey?: boolean;
    isSortKey?: boolean;
    isRequired?: boolean;
    isPII?: boolean;
}

export interface AssistantSchemaInput {
    name: string;
    description?: string;
    fields: AssistantFieldInput[];
}

export function buildFieldPayload(field: AssistantFieldInput): Record<string, unknown> {
    const payload: Record<string, unknown> = {
        fieldId: "",
        name: field.name,
        dataType: field.dataType,
        description: field.description ?? "",
        active: true,
        isPrimaryKey: !!field.isPrimaryKey,
        isSortKey: !!field.isSortKey,
        isRequired: !!field.isRequired,
        isPII: !!field.isPII,
    };

    // Joi requires `allowedBooleanValues` whenever dataType === 'boolean'.
    if (field.dataType === "boolean") {
        payload.allowedBooleanValues = "Y/N";
    }

    return payload;
}

export function buildSchemaPayload(schema: AssistantSchemaInput, businessAreaId: string): Record<string, unknown> {
    return {
        schemaId: "",
        name: schema.name,
        description: schema.description ?? "",
        businessAreaId,
        active: true,
        fields: (schema.fields || []).map(buildFieldPayload),
    };
}

export function buildUpdateFieldPayload(existing: FetchedField, change: AssistantUpdateFieldInput): Record<string, unknown> {
    if (existing.version === undefined || existing.version === null) {
        throw new Error(`Field "${existing.name}" is missing version metadata; cannot update.`);
    }
    const payload: Record<string, unknown> = {
        fieldId: existing.fieldId,
        version: existing.version,
        // dataType is required by updateFieldSchema even when only flags change.
        // Send the new type when the user requested a change, else keep existing.
        dataType: change.dataType !== undefined ? change.dataType : existing.dataType,
    };
    if (change.newName !== undefined) payload.name = change.newName;
    if (change.description !== undefined) payload.description = change.description;
    if (change.isPrimaryKey !== undefined) payload.isPrimaryKey = change.isPrimaryKey;
    if (change.isSortKey !== undefined) payload.isSortKey = change.isSortKey;
    if (change.isRequired !== undefined) payload.isRequired = change.isRequired;
    if (change.isPII !== undefined) payload.isPII = change.isPII;
    if (existing.dataType === "boolean" && existing.allowedBooleanValues) {
        payload.allowedBooleanValues = existing.allowedBooleanValues;
    }
    return payload;
}

export function buildDeleteFieldPayload(existing: FetchedField): Record<string, unknown> {
    if (existing.version === undefined || existing.version === null) {
        throw new Error(`Field "${existing.name}" is missing version metadata; cannot delete.`);
    }
    return { fieldId: existing.fieldId, version: existing.version };
}

export function buildSchemaUpdateRequest(
    existing: { version?: number },
    changes: { name?: string; description?: string },
): Record<string, unknown> | null {
    if (changes.name === undefined && changes.description === undefined) return null;
    if (existing.version === undefined || existing.version === null) {
        throw new Error("Schema is missing version metadata; cannot update.");
    }
    const out: Record<string, unknown> = { version: existing.version };
    if (changes.name !== undefined) out.name = changes.name;
    if (changes.description !== undefined) out.description = changes.description;
    return out;
}
